/** Que áreas o usuário alcança e por onde ele entra em cada uma.
 *
 *  Quem decide é o servidor: `areas` na sessão já são as EFETIVAS, isto é, as
 *  concedidas pelo cargo ou pelo próprio usuário em que ele tem ao menos uma leitura
 *  de módulo. Aqui a interface só cruza isso com o menu, para nunca mostrar um card
 *  que abriria uma área sem tela nenhuma: área sem tela alcançável não conta, e o
 *  destino vira `/sem-acesso`, nunca a raiz da área, o que fecharia um laço.
 *
 *  Funções puras, sem React, para `paths.ts`, os portões e os testes usarem. */
import { flattenNav, type NavItem } from '../router/navConfig'
import { HUBS, type HubKey } from '../hubs/hubs'
import { AREAS, areaPorChave, ehAreaKey, hubDaArea, type AreaKey } from './areas'

/** O que estas funções precisam saber do usuário. */
export interface UsuarioComAreas {
  is_staff?: boolean
  is_superuser?: boolean
  role?: string
  permissions?: string[]
  areas?: string[]
}

export function ehSuperusuario(usuario: UsuarioComAreas | null | undefined): boolean {
  return Boolean(usuario?.is_superuser) || usuario?.role === 'staff_admin'
}

/** O item do menu está ao alcance? Mesma regra da barra lateral e do PermissionGate. */
export function alcancaItem(usuario: UsuarioComAreas | null | undefined, item: NavItem): boolean {
  if (ehSuperusuario(usuario)) return true
  const concedidas = new Set(usuario?.permissions ?? [])
  if (item.permission && !concedidas.has(item.permission)) return false
  if (item.anyPermission && !item.anyPermission.some((key) => concedidas.has(key))) return false
  return true
}

/** As áreas que o servidor liberou, na ordem do catálogo. Cliente não tem área: o
 *  portal é outra entrada. */
export function areasLiberadas(usuario: UsuarioComAreas | null | undefined): AreaKey[] {
  if (!usuario?.is_staff) return []
  if (ehSuperusuario(usuario)) return AREAS.map((a) => a.key)
  const doServidor = new Set((usuario.areas ?? []).filter(ehAreaKey))
  return AREAS.map((a) => a.key).filter((key) => doServidor.has(key))
}

/** A primeira tela da área que o usuário alcança, na ordem do menu da área. */
export function primeiraTelaDaArea(area: AreaKey, usuario: UsuarioComAreas | null | undefined): string | null {
  const item = flattenNav(areaPorChave(area).nav).find((i) => alcancaItem(usuario, i))
  return item ? item.path : null
}

/** As áreas liberadas que têm ao menos uma tela ao alcance: as que viram card,
 *  entrada no seletor e destino depois do login. */
export function areasNavegaveis(usuario: UsuarioComAreas | null | undefined): AreaKey[] {
  return areasLiberadas(usuario).filter((area) => primeiraTelaDaArea(area, usuario) !== null)
}

// ── Os hubs ──────────────────────────────────────────────────────────

/** Os hubs que as áreas cobrem, na ordem do catálogo de hubs. É a mesma derivação
 *  que o servidor faz para o `hubs` da sessão. */
export function hubsDasAreas(areas: readonly string[]): HubKey[] {
  const cobertos = new Set(areas.filter(ehAreaKey).map(hubDaArea))
  return HUBS.map((h) => h.key).filter((key) => cobertos.has(key))
}

/** Os hubs em que o usuário alcança ao menos uma tela. O superusuário entra nos
 *  dois; quem tem um hub só não vê a troca de hub. */
export function hubsNavegaveis(usuario: UsuarioComAreas | null | undefined): HubKey[] {
  return hubsDasAreas(areasNavegaveis(usuario))
}

/** As áreas navegáveis de um hub só: os cards da tela de áreas e o seletor de área
 *  da barra lateral nunca misturam os dois hubs. */
export function areasDoHub(usuario: UsuarioComAreas | null | undefined, hub: HubKey): AreaKey[] {
  return areasNavegaveis(usuario).filter((area) => hubDaArea(area) === hub)
}

/** O hub em que a pessoa de fato fica: o pedido, se ela o alcança; senão o primeiro
 *  que ela alcança. Sem hub nenhum, o pedido, e o destino vira `/sem-acesso`. */
export function hubAlcancavel(usuario: UsuarioComAreas | null | undefined, pedido: HubKey): HubKey {
  const hubs = hubsNavegaveis(usuario)
  return hubs.includes(pedido) ? pedido : (hubs[0] ?? pedido)
}

// ── A área atual em rota neutra ──────────────────────────────────────

const AREA_ATUAL_KEY = 'tg.area.atual'

/** Guarda a última área visitada na sessão da aba. Serve às rotas que não são de
 *  área nenhuma, como `/conta/configuracoes`: a barra lateral continua mostrando a
 *  área de onde a pessoa veio. */
export function lembrarArea(area: AreaKey): void {
  try {
    sessionStorage.setItem(AREA_ATUAL_KEY, area)
  } catch {
    /* sem storage, a rota neutra cai na primeira área */
  }
}

export function ultimaArea(): AreaKey | null {
  try {
    const valor = sessionStorage.getItem(AREA_ATUAL_KEY)
    return ehAreaKey(valor) ? valor : null
  } catch {
    return null
  }
}
